export type SalesLeadStatus = 'new' | 'contacted' | 'negotiation' | 'won' | 'lost' | 'disqualified';

export type SalesSection = 'pipeline' | 'archived' | 'generator' | 'settings';

export interface SalesLead {
  id: string;
  org_id: string;
  company_name: string;
  website_url: string | null;
  business_info: string | null;
  inquiry: string | null;
  contact_email: string | null;
  contact_name: string | null;
  contact_phone: string | null;
  ai_rating: number | null;
  ai_analysis: string | null;
  sales_rating: number | null;
  source: string;
  status: SalesLeadStatus;
  archived: boolean;
  /** Kanban order within the same status column (lower = higher in column). */
  pipeline_order: number;
  created_at: string;
  updated_at: string;
}

export interface SalesLeadListResponse {
  leads: SalesLead[];
  total: number;
  page: number;
  per_page: number;
}

export interface SalesComment {
  id: string;
  lead_id: string;
  user_id: string | null;
  content: string;
  is_agent: boolean;
  reply_to_id: string | null;
  created_at: string;
  username: string | null;
  name: string | null;
}

export interface SalesLeadExample {
  id: string;
  type: 'good' | 'bad';
  company_url: string;
  explanation: string;
  created_by: string;
  created_at: string;
}

export interface SalesCompanyProfile {
  id: string;
  org_id: string;
  description: string;
  updated_at: string;
  updated_by: string | null;
}

export interface SalesStatusHistoryEntry {
  id: string;
  lead_id: string;
  user_id: string | null;
  old_status: string | null;
  new_status: string;
  changed_at: string;
  username: string | null;
  name: string | null;
}

export const KANBAN_COLUMNS: SalesLeadStatus[] = [
  'new', 'contacted', 'negotiation', 'won', 'lost', 'disqualified',
];

export const STATUS_CONFIG: Record<SalesLeadStatus, { label: string; labelKey: string; color: string }> = {
  new: { label: 'New', labelKey: 'salesStatusNew', color: 'text-status-info' },
  contacted: { label: 'Contacted', labelKey: 'salesStatusContacted', color: 'text-amber-500' },
  negotiation: { label: 'Negotiation', labelKey: 'salesStatusNegotiation', color: 'text-purple-500' },
  won: { label: 'Won', labelKey: 'salesStatusWon', color: 'text-status-success' },
  lost: { label: 'Lost', labelKey: 'salesStatusLost', color: 'text-status-error' },
  disqualified: { label: 'Disqualified', labelKey: 'salesStatusDisqualified', color: 'text-content-muted' },
};

// ── Lead Generator types ──

export type LeadQuality = 'high' | 'medium' | 'low';

export interface GeneratedLead {
  company_name: string;
  website_url: string;
  business_info: string;
  inquiry: string;
  contact_email: string;
  contact_name: string;
  ai_rating: number;
  quality: LeadQuality;
  ai_analysis: string;
}

export interface LeadGenerateParams {
  country: string;
  state?: string;
  city?: string;
  distance_km?: number;
  industry_keywords?: string;
  max_results: number;
}

export interface GeneratorSSEEvent {
  type: 'status' | 'lead_found' | 'complete' | 'error';
  message?: string;
  lead?: GeneratedLead;
  index?: number;
  total_found?: number;
  summary?: string;
}

export interface DiscoveredLead extends GeneratedLead {
  selected: boolean;
}

export type GeneratorPhase = 'config' | 'running' | 'results';

export const STATUS_DOT_COLOR: Record<SalesLeadStatus, string> = {
  new: 'bg-blue-400',
  contacted: 'bg-amber-400',
  negotiation: 'bg-purple-400',
  won: 'bg-emerald-400',
  lost: 'bg-red-400',
  disqualified: 'bg-content-muted',
};
